import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/constants';
import { hybridStorage } from '@/utils/storage';

export type ThemeMode = 'light' | 'dark';

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface UiState {
  themeMode: ThemeMode;
  sidebarOpen: boolean;
  toast: ToastState;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  showToast: (message: string, severity?: ToastState['severity']) => void;
  hideToast: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      themeMode: 'light',
      sidebarOpen: true,
      toast: { open: false, message: '', severity: 'info' },
      setThemeMode: (themeMode) => set({ themeMode }),
      toggleTheme: () =>
        set({ themeMode: get().themeMode === 'light' ? 'dark' : 'light' }),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      showToast: (message, severity = 'info') =>
        set({ toast: { open: true, message, severity } }),
      hideToast: () => set({ toast: { ...get().toast, open: false } }),
    }),
    {
      name: STORAGE_KEYS.ui,
      storage: createJSONStorage(() => hybridStorage),
      version: 2,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as { themeMode?: ThemeMode; sidebarOpen?: boolean };
        if (!version || version < 2) {
          return {
            ...state,
            themeMode: 'light',
          };
        }
        return state;
      },
      partialize: (state) => ({
        themeMode: state.themeMode,
        sidebarOpen: state.sidebarOpen,
      }),
    },
  ),
);
